import {
  NodeconfigV1NodeConfiguration,
  NodeconfigV1NewNodeConfiguration,
  NodeconfigV1NodeConfigurationUpdate,
} from '@cast/openapi';

export type {
  NodeconfigV1GetSuggestedConfigurationResponse as NodeConfigurationSuggestions,
  NodeconfigV1SecurityGroup as NodeConfigurationSecurityGroup,
  NodeconfigV1InstanceProfile as NodeConfigurationInstanceProfile,
  NodeconfigV1SubnetDetails as NodeConfigurationSubnetDetails,
  NodeconfigV1TargetGroup as NodeConfigurationTargetGroup,
  NodetemplatesV1TaintWithOptionalEffect as NodeTemplateTaintWithOptionalEffect,
  NodeconfigV1ListConfigurationsResponse as NodeConfigurationsListResponse,
} from '@cast/openapi';
export {
  NodeconfigV1ContainerRuntime as NodeConfigurationContainerRuntime,
  NodeconfigV1EKSConfigImageFamily as EKSNodeConfigurationImageFamily,
  NodeconfigV1AKSConfigImageFamily as AKSNodeConfigurationImageFamily,
} from '@cast/openapi';

type NodeConfigurationBase = Omit<NodeconfigV1NodeConfiguration, '_default'> & {
  default?: boolean;
};

type EKSConfiguration<T> = Omit<
  T,
  'aks' | 'gke' | 'kops' | 'selfHostedWithEc2Nodes'
>;
type GKEConfiguration<T> = Omit<
  T,
  'aks' | 'eks' | 'kops' | 'selfHostedWithEc2Nodes'
>;
type AKSConfiguration<T> = Omit<
  T,
  'eks' | 'gke' | 'kops' | 'selfHostedWithEc2Nodes'
>;
type KOPSConfiguration<T> = Omit<
  T,
  'aks' | 'gke' | 'eks' | 'selfHostedWithEc2Nodes'
>;
type SelfHostedConfiguration<T> = Omit<T, 'aks' | 'gke' | 'eks' | 'kops'>;

export type EKSNodeConfiguration = EKSConfiguration<NodeConfigurationBase>;
export type GKENodeConfiguration = GKEConfiguration<NodeConfigurationBase>;
export type AKSNodeConfiguration = AKSConfiguration<NodeConfigurationBase>;
/** @public */
export type KOPSNodeConfiguration = KOPSConfiguration<NodeConfigurationBase>;
export type SelfHostedNodeConfiguration =
  SelfHostedConfiguration<NodeConfigurationBase>;

export type NodeConfigurationTemplate =
  | EKSNodeConfiguration
  | GKENodeConfiguration
  | AKSNodeConfiguration
  | KOPSNodeConfiguration
  | SelfHostedNodeConfiguration;

type CreateEKSNodeConfiguration =
  EKSConfiguration<NodeconfigV1NewNodeConfiguration>;
type CreateGKENodeConfiguration =
  GKEConfiguration<NodeconfigV1NewNodeConfiguration>;
type CreateAKSNodeConfiguration =
  AKSConfiguration<NodeconfigV1NewNodeConfiguration>;
type CreateKOPSNodeConfiguration =
  KOPSConfiguration<NodeconfigV1NewNodeConfiguration>;
type CreateSelfHostedNodeConfiguration =
  SelfHostedConfiguration<NodeconfigV1NewNodeConfiguration>;

export type CreateNodeConfigurationTemplate =
  | CreateEKSNodeConfiguration
  | CreateGKENodeConfiguration
  | CreateAKSNodeConfiguration
  | CreateKOPSNodeConfiguration
  | CreateSelfHostedNodeConfiguration;

type UpdateEKSNodeConfiguration =
  EKSConfiguration<NodeconfigV1NodeConfigurationUpdate>;
type UpdateGKENodeConfiguration =
  GKEConfiguration<NodeconfigV1NodeConfigurationUpdate>;
type UpdateAKSNodeConfiguration =
  AKSConfiguration<NodeconfigV1NodeConfigurationUpdate>;
type UpdateKOPSNodeConfiguration =
  KOPSConfiguration<NodeconfigV1NodeConfigurationUpdate>;
type UpdateSelfHostedNodeConfiguration =
  SelfHostedConfiguration<NodeconfigV1NodeConfigurationUpdate>;

export type UpdateNodeConfigurationTemplate =
  | UpdateEKSNodeConfiguration
  | UpdateGKENodeConfiguration
  | UpdateAKSNodeConfiguration
  | UpdateKOPSNodeConfiguration
  | UpdateSelfHostedNodeConfiguration;

export const isEKSConfiguration = (
  config: NodeConfigurationTemplate
): config is EKSNodeConfiguration => {
  return (config as EKSNodeConfiguration).eks !== undefined;
};

/** @public */
export const isAKSConfiguration = (
  config: NodeConfigurationTemplate
): config is AKSNodeConfiguration => {
  return (config as AKSNodeConfiguration).aks !== undefined;
};

/** @public */
export const isKOPSConfiguration = (
  config: NodeConfigurationTemplate
): config is KOPSNodeConfiguration => {
  return (config as KOPSNodeConfiguration).kops !== undefined;
};

/** @public */
export const isGKEConfiguration = (
  config: NodeConfigurationTemplate
): config is GKENodeConfiguration => {
  return (
    !isEKSConfiguration(config) &&
    !isAKSConfiguration(config) &&
    !isKOPSConfiguration(config)
  );
};

/** @public */
export const isSelfHostedConfiguration = (
  config: NodeConfigurationTemplate
): config is SelfHostedNodeConfiguration => {
  return (
    (config as SelfHostedNodeConfiguration).selfHostedWithEc2Nodes !== undefined
  );
};
